import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Star, BadgeCheck, Clock, Flame } from 'lucide-react';
interface Testimonial {
  body: string;
  author: string;
  location: string;
  avatar: string;
  date: string;
}
const testimonials: Testimonial[] = [{
  author: 'Catalina Rojas Fuentes',
  location: 'Santiago, Chile',
  date: '12 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/women/65.jpg',
  body: 'Dudé harto antes de comprarlo, me parecía caro para algo digital. Pero lo comparé con lo que gasto en una botella de vino decente y dije ya, lo pruebo. Mi papá lleva toda la vida haciendo asado los domingos y siempre salía más o menos, nunca mal pero nunca increíble tampoco. Le regalé La Biblia del Asado Chileno para su cumpleaños y el primer domingo que lo aplicó la carne quedó tan blanda que mi mamá le preguntó si había comprado otro corte. No había comprado nada distinto. Solo aplicó lo de la salmuera y los tiempos del manual. Valió cada peso y más.'
}, {
  author: 'Sebastián Valdés Morales',
  location: 'Concepción, Chile',
  date: '10 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
  body: 'Le compré esto a mi papá sin mucha fe porque él no es muy de leer manuales ni tutoriales. Es de los que aprende haciendo y ya. Pero este manual está escrito diferente. No es técnico ni aburrido. Mi papá lo abrió en el celular mientras esperaba que prendieran las brasas y no lo soltó en una hora. Me dijo que era como hablar con alguien que sabe de verdad, no como leer un libro de cocina. Ese mismo día aplicó lo de las zonas de calor y el lomo vetado quedó perfecto. Mi papá tiene 58 años y aprendió algo nuevo sobre asado. Eso no tiene precio.'
}, {
  author: 'Javiera Espinoza Contreras',
  location: 'Viña del Mar, Chile',
  date: '11 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/women/90.jpg',
  body: "Mi papá es de los que cree que ya sabe todo del asado. Lleva 30 años prendiendo parrillas y no acepta consejos de nadie, menos de su hija. Cuando le mandé La Biblia del Asado al email me dijo gracias m'ija con cara de que lo iba a guardar sin abrir. Pero al rato me escribió preguntando de dónde había sacado lo de los cortes secretos del carnicero porque nunca había escuchado del abastero preparado de esa forma. Ese domingo invitó a los vecinos específicamente para mostrar lo que había aprendido. Mi papá de 61 años, emocionado como niño con manual nuevo. Fue lo mejor que le he regalado en años."
}, {
  author: 'Tomás Herrera Vásquez',
  location: 'Temuco, Chile',
  date: '8 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/men/52.jpg',
  body: 'Estuve dos días dándole vueltas antes de comprarlo. Me parecía bien pero seguía posponiendo. Finalmente lo compré el jueves en la noche pensando en el Día del Padre. Lo que no esperaba era que llegara instantáneo al email. En serio, en menos de un minuto ya lo tenía todo descargado y listo para regalar. El domingo mi papá hizo el asado con el método del fuego perfecto que viene en el bonus y por primera vez en mi vida no tuvimos que esperar 40 minutos extra porque las brasas no prendían bien. Empezamos a comer a la hora que era. Eso en mi familia es un milagro. Ojalá lo hubiera comprado antes.'
}, {
  author: 'Francisca Muñoz Ibáñez',
  location: 'Puerto Montt, Chile',
  date: '9 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/women/12.jpg',
  body: 'Soy súper desconfiada para comprar cosas por internet, especialmente productos digitales que no puedo ver antes de pagar. Lo que me convenció fue la garantía de 7 días. Dije, si no sirve pido el reembolso y listo. Eso me dio la confianza para intentarlo. Compré, llegó al tiro al email, todo en orden. El contenido es serio y específico para Chile, no es traducido del inglés ni adaptado de recetas argentinas. Habla de los cortes que uno encuentra en cualquier carnicería de acá. Mi papá lo leyó completo en dos días. Nunca había visto a mi papá leer algo completo en dos días. Eso lo dice todo.'
}, {
  author: 'Diego Castillo Reyes',
  location: 'La Serena, Chile',
  date: '13 de junio, 2024',
  avatar: 'https://randomuser.me/api/portraits/men/76.jpg',
  body: 'Mi papá ya ve videos de parrilla en YouTube hace años y aun así la carne le salía dura a veces. La diferencia con esto es que está todo junto, ordenado y adaptado a lo que uno compra en Chile. No hay que buscar en diez videos distintos ni aguantar publicidad ni encontrar que el corte que explican no existe en ninguna carnicería de por acá. Mi papá lo describe como tener un maestro parrillero chileno explicándole todo en un solo lugar. Lo tiene guardado en el celular y lo consulta cada domingo antes de prender la parrilla. Le regalé esto hace tres semanas y ya me pidió que le mandara el link para regalárselo a su compadre.'
}];
// Gold/ember stars that match the cinematic landing palette.
function GoldStars() {
  return <div className="flex items-center gap-0.5" aria-label="5 de 5 estrellas" data-id="element-232">
      {[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-bbq-gold" fill="currentColor" strokeWidth={0} data-id="element-233" />)}
    </div>;
}
function TestimonialCard({
  t
}: {
  t: Testimonial;
}) {
  return <figure className="bg-bbq-dark p-6 md:p-7 rounded-xl border border-bbq-gold/15 flex flex-col h-full shadow-[0_0_30px_rgba(0,0,0,0.3)]" data-id="element-234">
      {/* Header: avatar + name */}
      <div className="flex items-center gap-3 mb-4" data-id="element-235">
        <img src={t.avatar} alt={`Foto de ${t.author}`} className="w-12 h-12 rounded-full object-cover border-2 border-bbq-gold/40 shrink-0" loading="lazy" data-id="element-236" />
        <div className="min-w-0" data-id="element-237">
          <div className="flex items-center gap-1.5" data-id="element-238">
            <span className="text-white font-semibold truncate" data-id="element-239">
              {t.author}
            </span>
            <BadgeCheck size={15} className="text-bbq-gold shrink-0" aria-label="Compra verificada" data-id="element-240" />
          </div>
          <p className="text-gray-500 text-sm truncate" data-id="element-241">{t.location}</p>
        </div>
      </div>

      {/* Stars + date */}
      <div className="flex items-center justify-between mb-4" data-id="element-242">
        <GoldStars data-id="element-243" />
        <span className="text-gray-600 text-xs" data-id="element-244">{t.date}</span>
      </div>

      {/* Body */}
      <blockquote className="text-gray-300 text-sm leading-relaxed flex-1" data-id="element-245">
        {t.body}
      </blockquote>

      {/* Footer */}
      <figcaption className="mt-5 pt-4 border-t border-white/5" data-id="element-246">
        <span className="inline-flex items-center gap-1.5 text-xs text-bbq-gold font-medium" data-id="element-247">
          <BadgeCheck size={13} aria-hidden="true" data-id="element-248" />
          Compra verificada
        </span>
      </figcaption>
    </figure>;
}
export function TestimonialsSection() {
  // Duplicate the list so the mobile marquee loops seamlessly.
  const marqueeItems = [...testimonials, ...testimonials];
  return <section className="bg-bbq-surface py-12 md:py-24 px-4 border-y border-white/5 overflow-hidden" data-id="element-249">
      <div className="max-w-6xl mx-auto" data-id="element-250">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 md:mb-16" data-id="element-251">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-white uppercase tracking-wide mb-6 leading-tight" data-id="element-252">
            Lo que dicen las familias chilenas que ya lo regalaron
          </h2>
          <div className="flex items-center justify-center gap-3" data-id="element-253">
            <GoldStars data-id="element-254" />
            <span className="text-gray-300 text-sm font-medium" data-id="element-255">
              <span className="text-white font-semibold" data-id="element-256">4.9/5</span> · basado
              en cientos de compras
            </span>
          </div>
        </motion.div>
      </div>

      {/* MOBILE: auto-scrolling horizontal carousel */}
      <div className="md:hidden relative" aria-label="Testimonios de clientes" data-id="element-257">
        <motion.div className="flex gap-5 w-max" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: 60,
        ease: 'linear',
        repeat: Infinity
      }} data-id="element-258">
          {marqueeItems.map((t, index) => <div key={index} className="w-[300px] shrink-0" data-id="element-259">
              <TestimonialCard t={t} data-id="element-260" />
            </div>)}
        </motion.div>
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-bbq-surface to-transparent" data-id="element-261"></div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-bbq-surface to-transparent" data-id="element-262"></div>
      </div>

      {/* DESKTOP: grid */}
      <div className="hidden md:block max-w-6xl mx-auto" data-id="element-263">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-264">
          {testimonials.map((t, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index % 3 * 0.12
        }} data-id="element-265">
              <TestimonialCard t={t} data-id="element-266" />
            </motion.div>)}
        </div>
      </div>

      <div className="max-w-6xl mx-auto" data-id="element-267">
        {/* Urgency / social proof */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-10 md:mt-16 max-w-3xl mx-auto bg-bbq-dark border border-bbq-ember/30 rounded-2xl p-8 md:p-10 text-center shadow-[0_0_40px_rgba(232,98,45,0.08)]" data-id="element-268">
          <p className="text-xl md:text-2xl font-semibold text-white inline-flex items-start justify-center gap-3 leading-snug" data-id="element-269">
            <Clock size={26} className="text-bbq-ember shrink-0 mt-0.5" aria-hidden="true" data-id="element-270" />
            <span data-id="element-271">
              Más de{' '}
              <span className="text-bbq-gold font-bold" data-id="element-272">
                500 papás chilenos
              </span>{' '}
              ya tienen este manual.
            </span>
          </p>
          <p className="mt-4 text-lg md:text-xl text-gray-300 inline-flex items-start justify-center gap-3 leading-snug" data-id="element-273">
            <Flame size={24} className="text-bbq-ember shrink-0 mt-0.5" aria-hidden="true" data-id="element-274" />
            <span data-id="element-275">¿El tuyo va a ser el único que siga quemando la carne?</span>
          </p>
        </motion.div>
      </div>
    </section>;
}